/**
 * RPC client for the editor backend.
 * Write operations go through /rpc-write/, read operations through /rpc/.
 */

/** Custom error class that carries the HTTP status code. */
export class RpcError extends Error {
  public readonly status: number;

  constructor(status: number, method: string, detail: string) {
    super(`RPC ${method} failed (${status}): ${detail}`);
    this.name = 'RpcError';
    this.status = status;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

let authToken: string | null = typeof localStorage !== 'undefined' ? localStorage.getItem('auth-token') : null;

export function setAuthToken(token: string | null): void {
  authToken = token;
}

export function getAuthToken(): string | null {
  return authToken;
}

/** Silently file a bug report for an RPC error (fire-and-forget, never blocks or throws). */
function autoReportBug(method: string, status: number, detail: string): void {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    void fetch('/rpc/CreateBugReport', {
      method: 'POST',
      headers,
      keepalive: true,
      body: JSON.stringify({
        description: `[Auto-reported] RPC ${method} failed (${status}): ${detail}`,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        screenWidth: window.innerWidth,
        screenHeight: window.innerHeight,
        appLanguage: document.documentElement.lang || '',
      }),
    }).catch(() => {
      // Best-effort — silently ignore failures
    });
    window.dispatchEvent(new CustomEvent('bug:auto-reported'));
  } catch {
    // Best-effort — silently ignore failures
  }
}

/** Call the write backend via the /rpc-write/ nginx proxy. */
export async function rpc<T = unknown>(method: string, body: Record<string, unknown> = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (authToken) {
    headers['Authorization'] = `Bearer ${authToken}`;
  }

  let res: Response;
  try {
    res = await fetch(`/rpc/${method}`, {
      method: 'POST',
      headers,
      cache: 'no-store',
      body: JSON.stringify(body),
    });
  } catch (err) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('network:offline'));
    }
    throw err;
  }
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent('auth:signout'));
    }
    const detail = (data as { error?: string }).error || res.statusText;
    const isTermsRequired = res.status === 403 && detail.includes('TERMS_NOT_ACCEPTED');
    if (isTermsRequired) {
      window.dispatchEvent(new CustomEvent('auth:terms-required'));
    }
    // Auto-report backend errors (skip 401/auth, terms required, and CreateBugReport to avoid recursion)
    if (res.status !== 401 && !isTermsRequired && method !== 'CreateBugReport') {
      autoReportBug(method, res.status, detail);
    }
    throw new RpcError(res.status, method, detail);
  }
  return data as T;
}



/** Generate a UUID v4 */
export function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}

/** Format lap time in ms → M:SS.mmm */
export function formatTime(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return '—';
  const totalSec = ms / 1000;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}:${sec.toFixed(3).padStart(6, '0')}`;
}
